import {
  ComponentRenderData,
  extractPlasmicQueryData,
  PlasmicComponent,
  PlasmicRootProvider
} from "@plasmicapp/loader-nextjs";
import type { GetStaticPaths, GetStaticProps } from "next";
import * as React from "react";

import craft from "@pages/api/craft";
import QueryCategorySlugs from "@queries/QueryCategorySlugs.graphql";
import QueryHeaderFooter from "@queries/QueryHeaderFooter.graphql";
import QueryResourcesCategory from "@queries/resources/QueryResourcesCategory.graphql";

import { PLASMIC } from "@abnormal/plasmic-init";
import Error from "next/error";
import Head from "next/head";
import { useRouter } from "next/router";
import { Seomatic } from "react-seomatic";

import getGlobals from "@lib/getGlobals";

export default function Page(props: {
	plasmicData?: ComponentRenderData;
	queryCache?: Record<string, any>;
	category?: { seomatic: Record<string, string> };
}) {
	const { plasmicData, queryCache, category } = props;
	const router = useRouter();
	if (!plasmicData || plasmicData.entryCompMetas.length === 0) {
		return <Error statusCode={404} />;
	}
	const pageMeta = plasmicData.entryCompMetas[0];
	return (
		<PlasmicRootProvider
			loader={PLASMIC}
			prefetchedData={plasmicData}
			prefetchedQueryData={queryCache}
			pageParams={pageMeta.params}
			pageQuery={router.query}
		>
			<Seomatic Head={Head} {...category.seomatic} />
			<PlasmicComponent component={pageMeta.displayName} />
		</PlasmicRootProvider>
	);
}

export const getStaticProps: GetStaticProps = async (context) => {
	const { categorySlug } = context.params ?? {};
	const plasmicPath =
		typeof categorySlug === "string"
			? `/resources/category/${categorySlug}`
			: Array.isArray(categorySlug)
			? `/${categorySlug.join("/")}`
			: "/";

	const plasmicData = await PLASMIC.maybeFetchComponentData([
		plasmicPath,
		"Footer"
  ], {
    // deferChunks: true,
  });

	if (!plasmicData) {
		// non-Plasmic catch-all
		return { props: {} };
	}

	const response = await craft(QueryHeaderFooter);

	const {
		primaryNavigation,
		primaryCallToActions,
		persistentNav,
		globalSets
	} = response;

	const categoryResponse = await craft(QueryResourcesCategory, {
		categorySlug: categorySlug,
		group: ["resourceTypes", "topics", "industries"]
	});

	const { category } = categoryResponse;

	const globals = await getGlobals(globalSets);

	const pageMeta = plasmicData.entryCompMetas[0];
	// Cache the necessary data fetched for the page
	const queryCache = await extractPlasmicQueryData(
		<PlasmicRootProvider
			loader={PLASMIC}
			prefetchedData={plasmicData}
			pageParams={pageMeta.params}
		>
			<PlasmicComponent component={pageMeta.displayName} />
		</PlasmicRootProvider>
	);

	// Use revalidate if you want incremental static regeneration
	return {
		props: {
			plasmicData,
			queryCache,
			navigation: {
				primaryNavigation,
				primaryCallToActions
			},
			persistentNav,
			globals,
			headerTheme: "dark",
			category
		},
		revalidate: 60
	};
};

export const getStaticPaths: GetStaticPaths = async () => {
	// Fetch every possible path

	const response = await craft(QueryCategorySlugs, {
		group: ["resourceTypes", "topics", "industries"],
		limit: 15
	});

	const paths = [];

	response.categories.map((category) => {
		console.log(`Generating /resources/category/${category.slug}`);

		paths.push({
			params: { categorySlug: category.slug }
		});
	});

	return { paths, fallback: "blocking" };
};
